import React from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ISuggestion } from '../../context/SuggestionsContext';
import SuggestionCard from './SuggestionCard';

interface BlockingSuggestionsModalProps {
    isOpen: boolean;
    blockingSuggestions: ISuggestion[];
    refreshSuggestions: () => void;
    dismissible?: boolean;
    onDismiss?: () => void;
}

const BlockingSuggestionsModal: React.FC<BlockingSuggestionsModalProps> = ({
    isOpen,
    blockingSuggestions,
    refreshSuggestions,
    dismissible = false,
    onDismiss
}) => {
    const isMobile = window.innerWidth <= 768;

    const handleDismiss = () => {
        if (dismissible && onDismiss) {
            onDismiss();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open && dismissible) handleDismiss();
            }}
        >
            <DialogContent
                className={`max-w-[800px] w-[90vw] max-h-[90vh] p-0 overflow-hidden md:w-[90vw] [&>button:last-child]:hidden`}
                onInteractOutside={(e) => { if (!dismissible) e.preventDefault(); }}
            >
                {/* Hidden title for accessibility */}
                <DialogTitle className="sr-only">Action Required</DialogTitle>

                <div className={`flex flex-col h-full relative ${isMobile ? 'p-4' : 'p-8'}`}>
                    {/* Close button when dismissible */}
                    {dismissible && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleDismiss}
                            className={`absolute z-[1000] ${isMobile ? 'top-2 right-2' : 'top-4 right-4'}`}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}

                    {/* Header */}
                    <div className={`border-b-2 border-destructive ${isMobile ? 'mb-4 pb-3' : 'mb-6 pb-4'}`}>
                        <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
                            <div className={`rounded-full bg-destructive flex items-center justify-center text-white font-bold flex-shrink-0 ${isMobile ? 'w-5 h-5 text-sm' : 'w-6 h-6 text-base'}`}>
                                !
                            </div>
                            <div>
                                <p className={`font-semibold text-destructive m-0 ${isMobile ? 'text-lg' : 'text-xl'}`}>
                                    Action Required
                                </p>
                                <p className={`text-muted-foreground m-0 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                    Please complete the following critical notifications before proceeding
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable content */}
                    <div className={`flex-1 overflow-y-auto overflow-x-hidden flex flex-col ${isMobile ? 'gap-3 pr-1' : 'gap-4 pr-2'}`}>
                        {blockingSuggestions.map((suggestion) => (
                            <div
                                key={suggestion.Id}
                                className={`border-2 border-destructive rounded-lg bg-muted ${isMobile ? 'p-1' : 'p-2'}`}
                            >
                                <SuggestionCard
                                    suggestion={suggestion}
                                    size="full"
                                    refreshParent={refreshSuggestions}
                                    showDocuSignMessage={false}
                                    adminMode={false}
                                    isViewMoreMobile={isMobile}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className={`border-t border-border text-center italic text-muted-foreground ${isMobile ? 'pt-3 pb-2 mt-3 text-[11px]' : 'pt-4 pb-4 mt-4 text-xs'}`}>
                        This dialog will automatically close once all critical notifications are resolved
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default BlockingSuggestionsModal;
